import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSquare, faSquareCheck, faTag} from "@fortawesome/free-solid-svg-icons";
import * as Constants from "../utils/Constants";
import {getAuthToken, getClientIdOfUser} from "../utils/utility";
import {CardList} from "./CardView";
import LabelMultiSelect from "./common/LabelMultiSelect";

/**
 * Reusable asset picker for the Reports page (points 6 & 11).
 *
 * Wraps the existing {@link CardList} with:
 *  - label-based filtering of the asset list (resolved via the asset-labels endpoint), and
 *  - a Select all / Deselect all control that operates on the currently visible (label-filtered)
 *    assets.
 *
 * Selection state (`checkedItems`, keyed by assetUnitId) is owned by the parent so existing search
 * logic keeps working unchanged.
 */
export default function ReportAssetFilter({assets, checkedItems, setCheckedItems}) {
  const [allLabels, setAllLabels] = useState([]);
  const [selectedLabelIds, setSelectedLabelIds] = useState([]);
  const [labelAssetIds, setLabelAssetIds] = useState(null); // Set<assetId> or null (no filter)

  // Load the client's labels once.
  useEffect(() => {
    const load = async () => {
      try {
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/asset-labels`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const res = await axios.get(url);
        if (res.status === 200) setAllLabels(res.data.responseData || []);
      } catch {
        /* labels are optional — ignore */
      }
    };
    load();
  }, []);

  // Resolve selected labels → asset ids whenever the selection changes.
  useEffect(() => {
    const resolve = async () => {
      if (selectedLabelIds.length === 0) {
        setLabelAssetIds(null);
        return;
      }
      try {
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/asset-labels/assets`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const res = await axios.get(url, {params: {labelIds: selectedLabelIds.join(","), size: 1000}});
        const ids = (res.data.responseData || []).map((d) => d.asset?.assetId).filter((v) => v != null);
        setLabelAssetIds(new Set(ids));
      } catch {
        setLabelAssetIds(new Set()); // fail closed — show nothing rather than the wrong set
      }
    };
    resolve();
  }, [selectedLabelIds]);

  const visibleAssets = useMemo(() => {
    if (!labelAssetIds) return assets;
    return (assets || []).filter((a) => labelAssetIds.has(a.assetId));
  }, [assets, labelAssetIds]);

  const toggleLabel = (labelId) => {
    setSelectedLabelIds((prev) =>
      prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]
    );
  };

  const toggleCheckbox = (assetUnitId) => {
    setCheckedItems((prev) => ({...prev, [assetUnitId]: !prev[assetUnitId]}));
  };

  const visibleCheckedCount = visibleAssets.filter((a) => checkedItems[a.assetUnitId]).length;
  const allVisibleSelected = visibleAssets.length > 0 && visibleCheckedCount === visibleAssets.length;

  const toggleSelectAll = () => {
    setCheckedItems((prev) => {
      const next = {...prev};
      const target = !allVisibleSelected;
      visibleAssets.forEach((a) => {
        next[a.assetUnitId] = target;
      });
      return next;
    });
  };

  return (
    <div className="report-asset-filter">
      {allLabels.length > 0 && (
        <div className="report-label-filter">
          <span className="filter-sub-label">
            <FontAwesomeIcon icon={faTag} /> Filter by label
          </span>
          <LabelMultiSelect
            labels={allLabels}
            selectedIds={selectedLabelIds}
            onToggle={toggleLabel}
          />
        </div>
      )}

      <button type="button" className="report-select-all" onClick={toggleSelectAll} disabled={visibleAssets.length === 0}>
        <FontAwesomeIcon icon={allVisibleSelected ? faSquareCheck : faSquare} />
        {allVisibleSelected ? "Deselect all" : "Select all"}
        {visibleAssets.length > 0 && <span className="report-select-count">({visibleCheckedCount}/{visibleAssets.length})</span>}
      </button>

      <div className="asset-list-wrapper">
        <CardList data={visibleAssets} checkedItems={checkedItems} toggleCheckbox={toggleCheckbox} />
      </div>
    </div>
  );
}
